import {
  ButtonGroup,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import InputMaskDocument from 'components/InputMaskDocument';
import InputNumber from 'components/InputNumber';
import LateChargesFields from 'components/LateChargesFields';
import { ReceivablesSummary } from 'components/receivables/ReceivablesSummary';
import useAuth from 'contexts/useAuth';
import { queryClient } from 'index';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { accountFeatures, createCharge } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { LATE_FIELD_PATH, lateChargesFieldErrors, lateChargesPlaceholders, lateFormOf } from 'utils/lateCharges';
import { onlyNumbers } from 'utils/number';
import { buildCreateChargeBody, CreateChargeFormData } from './createChargeBody';

interface CreatedLink {
  id: string;
  integrationId: string;
  amount: number;
  clientId: string;
  status: string;
  createdAt: string;
  itens: Iten[];
  chargeType: string;
  description: string;
  qrCode: string;
  txId: string;
  expirationDate: string;
  link: string;
  billet?: { id: string; billetDigit?: string };
}

interface Iten {
  id: string;
  chargeId: string;
  amount: string;
  name: string;
  quantity: number;
  unitPrice: string;
  feePercent: string;
}

const CreateCharge: React.FC<{}> = () => {
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [showPayer, setShowPayer] = React.useState(false);
  const [showIten, setShowIten] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [infoCobranca, setInfoCobranca] = React.useState<CreatedLink | null>(null);
  const [feeFromPayer, setFeeFromPayer] = React.useState(false);

  const navigate = useNavigate();
  const { userData } = useAuth();
  // Mesma regra da barra lateral (Carnês): boleto só com a feature ligada.
  const billetEnabled = !!userData?.features?.billet;
  // Mesma chave das Configurações e do carnê: na prática sai do cache.
  const { data: features } = useQuery(['features'], accountFeatures, { staleTime: 5 * 60 * 1000 });

  const { control, handleSubmit, reset, setValue, trigger, getValues, setError, clearErrors } =
    useForm<CreateChargeFormData>({
      defaultValues: {
        maxInstallments: 12,
        paymentMethod: 'PIX',
        latePenaltyMode: 'PERCENT',
        latePenaltyPercent: '',
        latePenaltyAmount: undefined,
        lateMonthlyInterestPercent: '',
      },
    });

  const paymentMethod = useWatch({ control, name: 'paymentMethod' });
  const isBillet = paymentMethod === 'BILLET';

  const watchedAmount = useWatch({ control, name: 'amount' });
  const watchedMaxInstallments = useWatch({ control, name: 'maxInstallments' });

  const createNewCharge = async (data: CreateChargeFormData) => {
    setLoading(true);
    try {
      const charge = await createCharge(buildCreateChargeBody(data, { feeFromPayer, showPayer }));
      setInfoCobranca(charge);
      queryClient.refetchQueries({
        queryKey: ['findAllCharges'],
        type: 'active',
      });
      toast({
        title: 'Sucesso',
        description: charge?.billet ? 'Boleto emitido com sucesso' : 'Cobrança criada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar cobrança',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = async (step: number) => {
    switch (step) {
      case 1:
        return await trigger(['amount', 'description', 'dueDate'] as const);
      case 2: {
        clearErrors(['latePenaltyPercent', 'latePenaltyAmount', 'lateMonthlyInterestPercent']);
        const errors = lateChargesFieldErrors(lateFormOf(getValues()), Number(getValues('amount')) || undefined);
        (Object.keys(errors) as Array<keyof typeof errors>).forEach((key) => {
          setError(LATE_FIELD_PATH[key], { message: errors[key] });
        });
        return Object.keys(errors).length === 0;
      }
      case 3: {
        const fields: Array<keyof CreateChargeFormData | `itens.${number}.description` | `itens.${number}.amount`> = [];
        // Boleto exige o pagador: o banco registra o título em nome dele.
        if (showPayer || isBillet) {
          fields.push('name', 'document');
        }
        if (showIten) {
          fields.push('itens.0.description', 'itens.0.amount');
        }
        return fields.length ? await trigger(fields) : true;
      }
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const nextStep = currentStep + 1;
    if (nextStep <= 3) {
      const isValid = await validateStep(currentStep);
      if (isValid) {
        setCurrentStep(nextStep);
      } else {
        toast({
          title: 'Erro',
          description: 'Por favor, preencha todos os campos obrigatórios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateProgress = () => {
    const totalSteps = 3;
    const completedSteps = currentStep - 1;
    return (completedSteps / totalSteps) * 100;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Dados básicos da cobrança</Text>
            <InputNumber
              control={control}
              placeholder="R$ 0,00"
              label={'Valor da cobrança'}
              name={`amount`}
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                },
              }}
            />
            <InputForm
              control={control}
              label="Descrição adicional"
              name="description"
              placeholder="Pagamento referência a..."
              type="text"
              rules={{
                required: 'Campo obrigatório',
              }}
            />
            <InputForm
              control={control}
              label="Data de vencimento"
              name="dueDate"
              type="date"
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  future: (value) => {
                    if (new Date(value) < new Date()) {
                      return 'A data de vencimento não pode ser anterior à data atual';
                    }
                    return true;
                  },
                },
              }}
            />
            {billetEnabled && (
              <VStack
                w="100%"
                align="flex-start"
                spacing={2}
              >
                <Text fontWeight="semibold">Forma de pagamento</Text>
                <ButtonGroup
                  w="100%"
                  isAttached
                >
                  <CustomButton
                    variant={isBillet ? 'outline' : 'solid'}
                    colorScheme="blue"
                    onClick={() => setValue('paymentMethod', 'PIX')}
                  >
                    Link com Pix
                  </CustomButton>
                  <CustomButton
                    variant={isBillet ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setValue('paymentMethod', 'BILLET')}
                  >
                    Boleto
                  </CustomButton>
                </ButtonGroup>
                {isBillet && (
                  <Text
                    fontSize="sm"
                    color="gray.600"
                  >
                    O boleto é registrado agora (a partir de R$ 5,00). No link o pagador ainda pode escolher Pix ou
                    cartão.
                  </Text>
                )}
              </VStack>
            )}
          </VStack>
        );
      case 2:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Configuração de taxas</Text>
            <VStack w={'100%'}>
              <HStack w={'100%'}>
                <ButtonGroup
                  w="100%"
                  isAttached
                >
                  <CustomButton
                    variant={feeFromPayer ? 'outline' : 'solid'}
                    colorScheme="blue"
                    onClick={() => setFeeFromPayer(false)}
                  >
                    Assumir taxas *
                  </CustomButton>
                  <CustomButton
                    variant={feeFromPayer ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setFeeFromPayer(true)}
                  >
                    Repassar taxas *
                  </CustomButton>
                </ButtonGroup>
              </HStack>
              <Text
                fontSize={'sm'}
                alignSelf={'flex-start'}
              >
                * Somente taxas de cartão de crédito
              </Text>
              <InputForm
                control={control}
                label="Máximo de parcelas"
                name="maxInstallments"
                placeholder="Máximo de parcelas"
                type="tel"
                rules={{
                  required: 'Campo obrigatório',
                  validate: {
                    positive: (value) => +value > 0 || 'O valor deve ser maior que 0',
                    max: (value) => +value <= 12 || 'O valor máximo de parcelas é 12',
                  },
                }}
              />
              <ReceivablesSummary
                amount={Number(watchedAmount) || 0}
                maxInstallments={Number(watchedMaxInstallments) || 0}
                feeType={feeFromPayer ? 'FROM_PAYER' : 'FROM_CUSTOMER'}
              />
              <VStack
                w="100%"
                align="stretch"
                spacing={1}
                pt={4}
              >
                <Text fontWeight="semibold">Juros e multa (opcional)</Text>
                <Text
                  fontSize="sm"
                  color="gray.600"
                >
                  Cobrados após o vencimento, no boleto e no Pix. Em branco usa o padrão da sua conta. Zero isenta.
                </Text>
              </VStack>
              <LateChargesFields
                control={control}
                nameMode="latePenaltyMode"
                namePenaltyPercent="latePenaltyPercent"
                namePenaltyAmount="latePenaltyAmount"
                nameInterest="lateMonthlyInterestPercent"
                placeholders={lateChargesPlaceholders(features?.billetLateCharges)}
                accountDefault={features?.billetLateCharges}
                amountLabel="Multa (R$) — até o valor da cobrança"
              />
            </VStack>
          </VStack>
        );
      case 3:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Informações adicionais</Text>
            {!isBillet && (
              <Checkbox
                width={'100%'}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setValue('name', '');
                    setValue('document', '');
                  }
                  setShowPayer(e.target.checked);
                }}
              >
                Informar dados do pagador?
              </Checkbox>
            )}
            {(showPayer || isBillet) && (
              <>
                <Text fontWeight="semibold">Dados do pagador</Text>
                <InputForm
                  control={control}
                  label="Nome"
                  name="name"
                  type="text"
                  placeholder="Nome completo"
                  rules={{ required: isBillet ? 'Nome do pagador obrigatório no boleto' : false }}
                />
                <InputMaskDocument
                  control={control}
                  label="CPF/CNPJ"
                  placeholder="000.000.000-00"
                  name="document"
                  type="tel"
                  rules={{
                    required: true,
                    validate: (value) => {
                      value = onlyNumbers(value);
                      if (validCnpj(value)) return true;
                      if (validCpf(value)) return true;
                      return 'Documento não é válido';
                    },
                  }}
                />
              </>
            )}
            <Checkbox
              width={'100%'}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('itens', []);
                }
                setShowIten(e.target.checked);
              }}
            >
              Adicionar item a cobrança?
            </Checkbox>
            {showIten && (
              <>
                <Text fontWeight="semibold">Itens</Text>
                <InputForm
                  control={control}
                  label="Descrição"
                  name="itens[0].description"
                  type="text"
                  placeholder="Descrição do item"
                  rules={{
                    required: 'Campo obrigatório',
                  }}
                />
                <InputNumber
                  control={control}
                  placeholder="R$ 0,00"
                  label={'Valor do item'}
                  name={`itens[0].amount`}
                  rules={{
                    required: 'Campo obrigatório',
                    validate: {
                      positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                    },
                  }}
                />
              </>
            )}
          </VStack>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CustomButton
        colorScheme="brand"
        variant="solid"
        onClick={() => {
          setShowPayer(false);
          setShowIten(false);
          onOpen();
          reset({
            maxInstallments: 12,
            paymentMethod: 'PIX',
            latePenaltyMode: 'PERCENT',
            latePenaltyPercent: '',
            latePenaltyAmount: undefined,
            lateMonthlyInterestPercent: '',
          });
          setInfoCobranca(null);
          setCurrentStep(1);
        }}
      >
        Criar cobrança
      </CustomButton>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setInfoCobranca(null);
            setCurrentStep(1);
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Criar cobrança</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {infoCobranca?.id ? (
                <VStack
                  spacing={5}
                  alignItems="center"
                >
                  <Text>
                    {infoCobranca?.billet
                      ? 'Boleto emitido com sucesso. Compartilhe o link ou abra o boleto:'
                      : 'Cobrança criada com sucesso, compartilhe o link abaixo:'}
                  </Text>
                  <CustomButton
                    colorScheme="blue"
                    variant="solid"
                    onClick={() => {
                      navigator.clipboard.writeText(infoCobranca?.link);
                    }}
                  >
                    Copiar link
                  </CustomButton>
                  {infoCobranca?.billet?.billetDigit && (
                    <VStack
                      spacing={2}
                      alignItems="center"
                    >
                      <Text fontWeight="semibold">Linha digitável</Text>
                      <Text
                        fontSize="sm"
                        textAlign="center"
                        wordBreak="break-all"
                        userSelect="text"
                      >
                        {infoCobranca.billet.billetDigit}
                      </Text>
                      <CustomButton
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(infoCobranca.billet?.billetDigit ?? '');
                        }}
                      >
                        Copiar linha digitável
                      </CustomButton>
                    </VStack>
                  )}
                  {infoCobranca?.billet && (
                    <CustomButton
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => navigate(`/access/charge/${infoCobranca.id}`)}
                    >
                      Ver boleto
                    </CustomButton>
                  )}
                </VStack>
              ) : (
                <>
                  <Progress
                    width={'100%'}
                    value={calculateProgress()}
                    mb={4}
                    sx={{
                      '& > div': {
                        transition: 'width 0.3s ease-in-out',
                      },
                    }}
                  />
                  {renderStepContent()}
                </>
              )}
            </ModalBody>

            <ModalFooter>
              {!infoCobranca && (
                <VStack
                  spacing={2}
                  w="100%"
                >
                  <HStack
                    w="100%"
                    justify="space-between"
                  >
                    {currentStep > 1 && (
                      <CustomButton
                        colorScheme="gray"
                        variant="outline"
                        onClick={handleBack}
                      >
                        Voltar
                      </CustomButton>
                    )}
                    {currentStep < 3 ? (
                      <CustomButton
                        colorScheme="blue"
                        variant="solid"
                        onClick={handleSubmit(handleNext)}
                      >
                        Próximo
                      </CustomButton>
                    ) : (
                      <CustomButton
                        colorScheme="green"
                        variant="solid"
                        isLoading={loading}
                        onClick={handleSubmit(createNewCharge)}
                      >
                        Criar cobrança
                      </CustomButton>
                    )}
                  </HStack>
                </VStack>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default CreateCharge;
